import { useState, useEffect } from 'react';
import { fetchPins, unpinMessage } from '@/lib/api';
import { toast } from '@/stores/toast.store';
import { useChatStore } from '@/stores/chat.store';
import type { PinnedMessageItem } from '@goodchat/shared/types';

interface PinnedMessagesBarProps {
  orgId: string;
  areaId: string;
  canUnpin?: boolean;
}

export function PinnedMessagesBar({ orgId, areaId, canUnpin }: PinnedMessagesBarProps) {
  const [pins, setPins] = useState<PinnedMessageItem[]>([]);
  const [expanded, setExpanded] = useState(false);
  // Store-driven live updates: app-shell bumps this on pin.added/removed WS
  // events (replaces the old __addPin DOM-method injection side room).
  const pinsVersion = useChatStore((s) => s.pinsVersionByArea[areaId] ?? 0);

  useEffect(() => {
    fetchPins(orgId, areaId)
      .then((res) => setPins(res.pins))
      .catch(() => toast.error('Failed to load pinned messages'));
  }, [orgId, areaId, pinsVersion]);

  const handleUnpin = async (pinId: string) => {
    try {
      await unpinMessage(orgId, areaId, pinId);
      setPins((prev) => prev.filter((p) => p.id !== pinId));
    } catch (err) {
      toast.error('Failed to unpin message');
    }
  };

  if (pins.length === 0) {
    return null;
  }

  return (
    <div className="border-b border-gray-200 bg-blue-50 dark:border-gray-700 dark:bg-blue-900/20">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-blue-700 hover:bg-blue-100 dark:text-blue-200 dark:hover:bg-blue-900/30"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.828.722a.5.5 0 01.354 0l.5.2A.5.5 0 0111 1.38V6a1 1 0 001 1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 00-1 1v4.618a.5.5 0 01-.318.458l-.5.2a.5.5 0 01-.354 0l-.5-.2A.5.5 0 019 15.618V11a1 1 0 00-1-1H6a1 1 0 01-1-1V7a1 1 0 011-1h2a1 1 0 001-1V1.382a.5.5 0 01.318-.458l.5-.2z" />
        </svg>
        {pins.length} pinned message{pins.length !== 1 ? 's' : ''}
        <svg
          className={`ml-auto h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="max-h-48 overflow-y-auto border-t border-blue-200 dark:border-blue-800">
          {pins.map((pin) => (
            <div
              key={pin.id}
              className="flex items-start gap-3 px-4 py-2 text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {pin.message.author.displayName}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    pinned by {pin.pinnedBy.displayName}
                  </span>
                </div>
                <p className="mt-0.5 truncate text-gray-600 dark:text-gray-300">
                  {pin.message.content ?? '[deleted]'}
                </p>
              </div>
              {canUnpin && (
                <button
                  onClick={() => handleUnpin(pin.id)}
                  className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700"
                  title="Unpin"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
